import React from 'react';
import { connect } from 'react-redux';
import {
  ItemsList,
  Size,
  BaseItem,
} from '../../../../components/ItemsPage/ItemsList';
import { Button } from '../../../../components/Button';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { PatientRouteParams } from '../../../router';
import { withTranslation, WithTranslation } from 'react-i18next';

import styles from './index.module.css';
import introStyles from '../../../FormStart/IntroPage/index.module.css';
import defaultStyle from '../../../../styles/formSetStyles.module.css';
import * as api from '../../../../api';
import * as store from '../../../../reducers/store';
import { PatientInsuranceInformation } from '../../../../api';

interface Props
  extends WithTranslation,
    StateProps,
    RouteComponentProps<PatientRouteParams> {
  cardErrors: Record<string, string>;
  onError?: (errorMessage: string) => void;
  navigateToUpload: (primary?: boolean) => void;
  navigateToEdit: (cardIndex: number) => void;
  navigateToNextPage: () => Promise<void>;
}

interface StateProps {
  cardsLoading: store.Loaded['insuranceCards']['loading'];
  cards: store.Loaded['insuranceCards']['data'];
  previsitPayorDisplayMapping: store.Loaded['masterPracticePreference']['previsitPayorDisplayMapping'];
}

interface State {
  items: BaseItem[];
}
function getLabelForInsurance(args: {
  card: api.PatientInsuranceInformation.InsuranceInformationWithDetails;
  cardNumber: number;
  t: WithTranslation['t'];
  previsitPayorDisplayMapping: Record<
    string | PatientInsuranceInformation.Payor,
    string
  >;
}) {
  const { card, cardNumber, t, previsitPayorDisplayMapping } = args;
  if (card.payor) {
    return (
      previsitPayorDisplayMapping[card.payor] ??
      api.Payor.payorMap[card.payor as PatientInsuranceInformation.Payor] ??
      String(card.payor).replaceAll('_', ' ')
    );
  }
  return `${t('Insurance #')}${cardNumber}`;
}

// display already-uploaded insurance card information, in order
class CardListPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      items: [],
    };
  }

  getItems() {
    const { cards, cardErrors, t } = this.props;
    return (cards || []).map((card, index) => {
      const label = getLabelForInsurance({
        card,
        cardNumber: index + 1,
        t,
        previsitPayorDisplayMapping: this.props.previsitPayorDisplayMapping,
      });
      const cardError = t(cardErrors[card.id]) ?? null;
      return {
        id: card.patientUploadId,
        primaryLabel: t(label),
        buttonText: t('edit', { ns: 'common' }),
        buttonInvertColors: true,
        children: cardError ? (
          <div className={styles.ErrorLabel}>{cardError}</div>
        ) : null,
      };
    });
  }

  render() {
    const { authenticationCode } = this.props.match.params;
    const { navigateToEdit, navigateToUpload, navigateToNextPage, t } =
      this.props;
    const items = this.getItems();
    const label = t('Do you have additional insurance you would like to add?');
    return (
      <div>
        <label className={introStyles.Label}>{label}</label>
        <ItemsList
          authenticationCode={authenticationCode}
          compact={true}
          items={items}
          primaryLabelSize={Size.Small}
          secondaryLabelSize={Size.Small}
          selectItemIndex={(index) => navigateToEdit(index)}
          className={introStyles.FormSelector}
        />
        <div className={defaultStyle.ButtonSubmitWrapper}>
          <Button
            type="button"
            children={t('Yes, add another')}
            onClick={() => navigateToUpload()}
            invertColors={false}
          />
          <Button
            type="button"
            children={t('No, continue')}
            onClick={navigateToNextPage}
            invertColors={false}
          />
        </div>
      </div>
    );
  }
}

const CardListPageWithRouter = withRouter(CardListPage);

const mapStateToProps = (state: store.Loaded): StateProps => ({
  cardsLoading: state.insuranceCards.loading,
  cards: state.insuranceCards.data,
  previsitPayorDisplayMapping:
    state.masterPracticePreference.previsitPayorDisplayMapping ?? {},
});

export default withTranslation()(
  connect(mapStateToProps)(CardListPageWithRouter),
);
